import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEnsureUser } from "./auth/hooks/useEnsureUser";
import Sidebar from "./components/layout/Sidebar";
import { FreelancerProvider } from "./contexts/useFreelancerContext";
import { ProjectFilterProvider } from "./contexts/ProjectFilterContext";
import { InternalUserProvider } from "./contexts/useInternalUserContext";
import { LinearProgressProvider } from "./contexts/useLinearProgressContext";
import { NavigationInterceptorProvider } from "./contexts/useNavigationInterceptorContext";
import { SnackbarProvider } from "./contexts/useSnackBarContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import { UserType } from "./models/UserType";
import Login from "./pages/login/index";
import PublicAnonymizedTalent from "./pages/public/PublicAnonymizedTalent";
import { isPublicRoute } from "./constants/publicRoutes";
import AppRoutes from "./routes/AppRoutes";
import { theme } from "./theme";
import { initGA, trackPageView } from "./utils/analytics";
import { initCookiebot } from "./utils/cookiebot";
import { initCrisp } from "./utils/crisp";
import { UserRole } from "./models/UserRole";

// Component to save the original URL before redirecting to login
const SaveUrlAndRedirect: React.FC = () => {
	const location = useLocation();

	useEffect(() => {
		// Save the current URL (path + search + hash) if it's not the login page
		if (location.pathname !== "/login") {
			const returnUrl = location.pathname + location.search + location.hash;
			sessionStorage.setItem("returnUrl", returnUrl);
		}
	}, [location]);

	return <Navigate to="/login" replace />;
};

// Component that conditionally wraps children with FreelancerProvider for Freelancer users only
// Uses useEnsureUser to get the same user data as AppRoutes (API data merged with token)
const ConditionalFreelancerProvider: React.FC<{ children: React.ReactNode }> = ({
	children
}) => {
	const { user, isLoading } = useEnsureUser();

	// While loading user data, don't render anything
	if (isLoading) {
		return null;
	}

	// Always wrap Freelancer users with FreelancerProvider
	// For other user types, render children directly
	if (user.type === UserType.Freelancer || user.roles?.includes(UserRole.TAM) === true) {
		return (
			<FreelancerProvider hasOwnFreelancerProfile={user.type === UserType.Freelancer}>
				{children}
			</FreelancerProvider>
		);
	}

	// For Internal and Client users, don't use FreelancerProvider
	return <>{children}</>;
};

// Initialize third-party scripts (Cookiebot first for consent management)
initCookiebot();
initGA();
initCrisp();

function App() {
	const location = useLocation();
	const { t, i18n } = useTranslation();

	// Track page views on route change
	useEffect(() => {
		trackPageView(location.pathname + location.search);
	}, [location]);

	// Update document title and meta description based on current language
	useEffect(() => {
		document.title = t("common.appTitle");
		const metaDescription = document.querySelector('meta[name="description"]');
		if (metaDescription) {
			metaDescription.setAttribute("content", t("common.appDescription"));
		}
	}, [t, i18n.language]);

	// Public surfaces (currently the anonymized talent fiche reached by
	// prospects via a token URL). These MUST bypass the auth gate completely
	// — they own their own layout and don't need the app shell. The full list
	// of public path prefixes lives in `constants/publicRoutes.ts` so adding a
	// new one is a deliberate, reviewable change.
	if (isPublicRoute(location.pathname)) {
		return (
			<LanguageProvider>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<Routes>
						<Route path="/p/talents/:token" element={<PublicAnonymizedTalent />} />
					</Routes>
				</ThemeProvider>
			</LanguageProvider>
		);
	}

	return (
		<LanguageProvider>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<APIProvider
					apiKey="AIzaSyCfQ317Pr4G2MUshqU3AULdZZeYR8cZKyY"
					libraries={["places"]}
					language={i18n.language}
				>
					<SnackbarProvider
						maxSnackbars={1}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						defaultDuration={4000}
					>
						<LinearProgressProvider>
							<NavigationInterceptorProvider>
								<InternalUserProvider>
									<ProjectFilterProvider>
										<div className="app">
											<AuthenticatedTemplate>
												<ConditionalFreelancerProvider>
													<Sidebar />
													<main className="content">
														<Routes>
															<Route path="/*" element={<AppRoutes />} />
														</Routes>
													</main>
												</ConditionalFreelancerProvider>
											</AuthenticatedTemplate>
											<UnauthenticatedTemplate>
												<Routes>
													<Route path="/login" element={<Login />} />
													<Route path="*" element={<SaveUrlAndRedirect />} />
												</Routes>
											</UnauthenticatedTemplate>
										</div>
									</ProjectFilterProvider>
								</InternalUserProvider>
							</NavigationInterceptorProvider>
						</LinearProgressProvider>
					</SnackbarProvider>
				</APIProvider>
			</ThemeProvider>
		</LanguageProvider>
	);
}

export default App;
